import { produce } from 'immer'
import {
  ExpenseReportResponse,
  ExpenseResponse,
} from 'src/models/expenseReport.model'
import {
  SET_IS_DELETE_EXPENSE_POPUP_OPEN,
  SET_IS_DELETE_EXPENSE_REPORTS_POPUP_OPEN,
  SET_IS_MARK_EXPENSE_REPORTS_PAID_POPUP_OPEN,
  SET_IS_SUBMIT_EXPENSE_REPORT_POPUP_OPEN,
  SetIsDeleteExpensePopupOpenAction,
  SetIsDeleteExpenseReportsPopupOpenAction,
  SetIsMarkExpenseReportsPaidPopupOpenAction,
  SetIsSubmitExpenseReportPopupOpenAction,
} from 'src/redux/actions/app.actions'
import {
  DELETE_EXPENSE_FAILURE,
  DELETE_EXPENSE_INIT,
  DELETE_EXPENSE_REPORTS_FAILURE,
  DELETE_EXPENSE_REPORTS_INIT,
  DELETE_EXPENSE_REPORTS_SUCCESS,
  DELETE_EXPENSE_SUCCESS,
  DeleteExpenseActions,
  DeleteExpenseReportsActions,
  GET_APPROVER_EXPENSE_REPORTS_SUCCESS,
  GET_SUBMITTER_EXPENSE_REPORTS_SUCCESS,
  GetApproverExpenseReportsActions,
  GetSubmitterExpenseReportsActions,
  PATCH_EXPENSE_SUCCESS,
  PatchExpenseActions,
  POST_CREATE_EXPENSE_REPORT_FAILURE,
  POST_CREATE_EXPENSE_REPORT_INIT,
  POST_CREATE_EXPENSE_REPORT_SUCCESS,
  POST_CREATE_EXPENSE_SUCCESS,
  POST_MARK_EXPENSE_REPORTS_PAID_FAILURE,
  POST_MARK_EXPENSE_REPORTS_PAID_INIT,
  POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS,
  PostCreateExpenseActions,
  PostCreateExpenseReportActions,
  PostMarkExpenseReportsPaidActions,
} from 'src/redux/actions/data/expenses.actions'

export interface ExpensesState {
  listResponses: Record<string, ExpenseReportResponse>
  latestListResponse?: ExpenseReportResponse
  detailReportResponse: Record<string, ExpenseReportResponse>
  detailExpenseResponse: Record<string, ExpenseResponse>
  postingExpense: boolean
  postingExpenseError?: string
  selectedMarkExpensesPaid?: string[]
  selectedMarkExpensesPaidAmount?: number
  postMarkingExpenseReportPaid: boolean
  postMarkingExpenseReportPaidError?: string
  selectedDeleteExpenses?: string[]
  deletingExpenses: boolean
  deletingExpensesError?: string
  selectedDeleteExpenseReports?: string[]
  deletingExpenseReports: boolean
  deletingExpenseReportsTopLevelError?: string
  numberOfExpenses: number | undefined
}

export const initialState: ExpensesState = {
  listResponses: {},
  latestListResponse: undefined,
  detailReportResponse: {},
  detailExpenseResponse: {},
  postingExpense: false,
  postMarkingExpenseReportPaid: false,
  deletingExpenses: false,
  deletingExpenseReports: false,
  numberOfExpenses: 0,
}

export type Actions =
  | PostCreateExpenseReportActions
  | PostCreateExpenseActions
  | GetSubmitterExpenseReportsActions
  | GetApproverExpenseReportsActions
  | SetIsMarkExpenseReportsPaidPopupOpenAction
  | PostMarkExpenseReportsPaidActions
  | PatchExpenseActions
  | DeleteExpenseActions
  | SetIsDeleteExpensePopupOpenAction
  | DeleteExpenseReportsActions
  | SetIsDeleteExpenseReportsPopupOpenAction
  | SetIsSubmitExpenseReportPopupOpenAction

const ExpensesReducer = (
  state: ExpensesState = initialState,
  action: Actions,
): ExpensesState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case GET_APPROVER_EXPENSE_REPORTS_SUCCESS:
      case GET_SUBMITTER_EXPENSE_REPORTS_SUCCESS: {
        draft.listResponses = action.payload.expenseReports.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.id]: curr,
          }),
          draft.listResponses,
        )
        return draft
      }

      case POST_CREATE_EXPENSE_REPORT_INIT: {
        draft.postingExpense = true
        draft.postingExpenseError = undefined
        return draft
      }

      case POST_CREATE_EXPENSE_REPORT_SUCCESS: {
        draft.postingExpense = false
        draft.latestListResponse = action.payload
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload.id]: action.payload,
        }
        return draft
      }

      case POST_CREATE_EXPENSE_REPORT_FAILURE: {
        draft.postingExpense = false
        draft.postingExpenseError = action.payload.error
        return draft
      }

      case PATCH_EXPENSE_SUCCESS:
      case POST_CREATE_EXPENSE_SUCCESS: {
        draft.detailExpenseResponse = {
          ...draft.detailExpenseResponse,
          [action.payload.id]: {
            ...draft.detailExpenseResponse[action.payload.id],
            ...action.payload,
          },
        }
        return draft
      }

      case SET_IS_MARK_EXPENSE_REPORTS_PAID_POPUP_OPEN: {
        draft.selectedMarkExpensesPaid = action.payload.expensesId
        draft.selectedMarkExpensesPaidAmount = action.payload.totalAmount
        draft.postingExpenseError = undefined

        return draft
      }

      case POST_MARK_EXPENSE_REPORTS_PAID_INIT: {
        draft.postMarkingExpenseReportPaid = true
        draft.postMarkingExpenseReportPaidError = undefined

        return draft
      }

      case POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS: {
        draft.postMarkingExpenseReportPaid = false
        draft.listResponses = action.payload.expenseReports.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.id]: curr,
          }),
          draft.listResponses,
        )
        return draft
      }

      case POST_MARK_EXPENSE_REPORTS_PAID_FAILURE: {
        draft.postMarkingExpenseReportPaid = false
        draft.postMarkingExpenseReportPaidError = action.payload.error

        return draft
      }

      case SET_IS_DELETE_EXPENSE_POPUP_OPEN: {
        draft.selectedDeleteExpenses = action.payload.selectedExpenses
        return draft
      }

      case SET_IS_DELETE_EXPENSE_REPORTS_POPUP_OPEN: {
        draft.selectedDeleteExpenseReports =
          action.payload.selectedExpenseReports
        return draft
      }

      case DELETE_EXPENSE_INIT: {
        draft.deletingExpenses = true
        draft.deletingExpensesError = undefined

        return draft
      }

      case DELETE_EXPENSE_SUCCESS: {
        draft.deletingExpenses = false
        draft.detailReportResponse = {
          ...draft.detailReportResponse,
          [action.payload.id]: action.payload,
        }
        return draft
      }

      case DELETE_EXPENSE_FAILURE: {
        draft.deletingExpenses = false
        draft.deletingExpensesError = action.payload.error

        return draft
      }

      case DELETE_EXPENSE_REPORTS_INIT: {
        draft.deletingExpenseReports = true
        draft.deletingExpenseReportsTopLevelError = undefined

        return draft
      }

      case DELETE_EXPENSE_REPORTS_SUCCESS: {
        draft.deletingExpenseReports = false
        draft.listResponses = Object.values(draft.listResponses)
          .filter(
            (cc) => !action.payload.expenseReports.deleted.includes(cc.id),
          )
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr.id]: curr,
            }),
            {},
          )

        return draft
      }

      case DELETE_EXPENSE_REPORTS_FAILURE: {
        draft.deletingExpenseReports = false
        draft.deletingExpenseReportsTopLevelError = action.payload.error
        return draft
      }

      case SET_IS_SUBMIT_EXPENSE_REPORT_POPUP_OPEN: {
        draft.numberOfExpenses = action.payload.numberOfExpenses
        return draft
      }
    }
  })
}

export default ExpensesReducer
